import * as React from "react";
import { Check, ChevronsUpDown, Search, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableCombobox - A combobox with search functionality
 * Used for searching through large lists of clients/cases
 *
 * Optional inline-create:
 *   onCreate          callback invoked when "+ Crea…" is clicked. Receives the
 *                     current search text so the parent can pre-fill the new
 *                     entity's form. Setting this prop enables the
 *                     "create new" button below the empty/result state.
 *   createButtonLabel template for the create button label. The substring
 *                     "{q}" is replaced by the trimmed search text in quotes,
 *                     or by an empty string if the search is empty.
 */
export function SearchableCombobox({
  items = [],
  value,
  onValueChange,
  placeholder = "Seleziona...",
  searchPlaceholder = "Cerca...",
  emptyMessage = "Nessun risultato trovato.",
  renderItem,
  renderSelectedItem,
  disabled = false,
  className,
  onCreate,
  createButtonLabel = "+ Crea nuovo {q}",
  "data-testid": dataTestId,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  // Find selected item
  const selectedItem = items.find((item) => item.value === value);

  // Filter items based on search
  const filteredItems = React.useMemo(() => {
    if (!searchValue) return items;
    const searchLower = searchValue.toLowerCase();
    return items.filter((item) => {
      const label = item.label?.toLowerCase() || "";
      const searchTerms = item.searchTerms?.toLowerCase() || "";
      return label.includes(searchLower) || searchTerms.includes(searchLower);
    });
  }, [items, searchValue]);

  const trimmedSearch = (searchValue || "").trim();
  // Show the create button when:
  //   - onCreate prop is wired AND
  //   - either the search yields zero results, or the search text doesn't
  //     exactly match any visible item's label (case-insensitive).
  const hasExactMatch = filteredItems.some(
    (it) => (it.label || "").toLowerCase() === trimmedSearch.toLowerCase()
  );
  const showCreateButton =
    typeof onCreate === "function" &&
    trimmedSearch.length > 0 &&
    !hasExactMatch;

  const handleCreateClick = () => {
    const q = trimmedSearch;
    setOpen(false);
    setSearchValue("");
    onCreate(q);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={dataTestId}
        >
          {selectedItem ? (
            renderSelectedItem ? (
              renderSelectedItem(selectedItem)
            ) : (
              <span className="truncate">{selectedItem.label}</span>
            )
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredItems.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={() => {
                    onValueChange(item.value === value ? "" : item.value);
                    setOpen(false);
                    setSearchValue("");
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 flex-shrink-0",
                      value === item.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {renderItem ? (
                    renderItem(item)
                  ) : (
                    <span className="truncate">{item.label}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            {showCreateButton && (
              <div className="p-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  onClick={handleCreateClick}
                  data-testid="searchable-combobox-create-btn"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  {createButtonLabel.replace(
                    "{q}",
                    trimmedSearch ? `"${trimmedSearch}"` : ""
                  )}
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
